import React, { useState, useRef, useEffect } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import { useNavigate } from "react-router-dom";
import { Logo } from "../../assets/svg/Logo";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { ChevronsUpDown } from "lucide-react";
import { SidebarMenuButton, useSidebar } from "@/components/ui/Sidebar";
import { Skeleton } from "@/components/ui/Skeleton";
import { useTranslation } from "react-i18next";
import { useUserOrganizations } from "@/hooks/useUserOrganizations";
import { Organization } from "@/types/Organization";
import { useOrg } from "@/hooks/useOrg";
import { ROUTES } from "@/routes/paths";

const ORG_SEARCH_THRESHOLD = 15;

export const OrganizationHeader = () => {
  const { data: orgList, loading: loadingGroupOrgs } = useUserOrganizations();

  // currently selected org
  const { org } = useOrg();

  const loading = loadingGroupOrgs || !orgList;

  return (
    <header>
      {(() => {
        if (loading) return <HeaderSkeleton />;

        if (org && orgList.length > 1) {
          return <MultiOrganizationHeader currentOrg={org} orgs={orgList} />;
        }
        return org && <SingleOrganizationHeader orgName={org.name} />;
      })()}
    </header>
  );
};

const SingleOrganizationHeader = ({ orgName }: { orgName: string }) => {
  return (
    <div className="flex h-auto gap-2 p-2">
      <HeaderContent orgName={orgName} />
    </div>
  );
};

const MultiOrganizationHeader = ({
  currentOrg,
  orgs,
}: {
  currentOrg: Organization;
  orgs: Organization[];
}) => {
  const { t } = useTranslation();
  const { open } = useSidebar();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleSelectOrg = (id: string) => {
    setDropdownOpen(false);
    navigate(ROUTES.ORGANIZATION.replace(":orgId", id));
  };

  const useSearch = orgs.length > ORG_SEARCH_THRESHOLD;

  return (
    <nav aria-label={t("nav.organization")}>
      <DropdownMenu
        modal={false}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
      >
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className="h-auto"
            aria-label={t("nav.organizationMenu")}
          >
            <HeaderContent orgName={currentOrg.name} />
            <ChevronsUpDown aria-hidden="true" className="ml-auto" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          alignOffset={70}
          className="w-[--radix-popper-anchor-width]"
          side={open ? "bottom" : "right"}
          align={open ? "start" : "start"}
        >
          {useSearch ? (
            <OrganizationSearch orgs={orgs} onSelectOrg={handleSelectOrg} />
          ) : (
            <OrganizationDropdown orgs={orgs} onSelectOrg={handleSelectOrg} />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
};

const OrganizationSearch = ({
  orgs,
  onSelectOrg,
}: {
  orgs: Organization[];
  onSelectOrg: (orgId: string) => void;
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [searchValue]);

  return (
    <Command tabIndex={-1}>
      <CommandInput
        ref={inputRef}
        placeholder={t("organizationHeader.selectOrg")}
        className="h-9"
        value={searchValue}
        onValueChange={setSearchValue}
      />
      <CommandList ref={listRef} aria-multiselectable={true}>
        <CommandEmpty>{t("organizationHeader.noOrgsFound")}</CommandEmpty>
        <CommandGroup aria-label={t("global.all")}>
          {orgs.map((org) => (
            <CommandItem
              key={org.id}
              value={org.name}
              onSelect={() => onSelectOrg(org.id)}
            >
              <OrgIcon org={org} />
              {org.name}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

const OrganizationDropdown = ({
  orgs,
  onSelectOrg,
}: {
  orgs: Organization[];
  onSelectOrg: (orgId: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="px-1 py-1.5 text-xs leading-5 font-semibold">
        {t("nav.schools")}
      </div>
      {orgs.map((org) => (
        <DropdownMenuItem key={org.id} onClick={() => onSelectOrg(org.id)}>
          <OrgIcon org={org} />
          <span>{org.name}</span>
        </DropdownMenuItem>
      ))}
    </>
  );
};

const HeaderContent = ({ orgName }: { orgName: string }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground">
        <Logo aria-hidden="true" />
        <span className="sr-only">{t("nav.zeeMeeLogo")}</span>
      </div>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="text-sm/3.5 font-semibold">{t("nav.title")}</span>
        <span className="truncate text-xs">{orgName}</span>
      </div>
    </>
  );
};

const HeaderSkeleton = () => {
  return (
    <SidebarMenuButton size="lg" asChild>
      <div>
        <div className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground">
          <Skeleton className="h-8 w-8 bg-sidebar-accent-dark" />
        </div>
        <div className="w-full pt-[2px] pr-6">
          <Skeleton className="mb-[2px] h-[14px] w-[100px] bg-sidebar-accent-dark" />
          <Skeleton className="h-3 w-full bg-sidebar-accent-dark" />
        </div>
      </div>
    </SidebarMenuButton>
  );
};

const OrgIcon = ({ org }: { org: Organization }) =>
  org.logo?.url && (
    <img
      className={"size-6 rounded-full"}
      alt={org.name}
      src={org.logo?.url || undefined}
    />
  );
